/**
 * Constraint Validation API helper.
 *
 * Attaches to any <form data-validate>. Sets `novalidate` so the browser's
 * native error UI is suppressed, then drives custom error messages from the
 * field's `validity` state into the [data-error-message] span referenced by
 * the field's `aria-describedby`.
 *
 * No-JS fallback: the HTML constraint attributes (required, type="email",
 * pattern, minlength, etc.) stay in the markup and the browser handles
 * validation natively.
 *
 * AJAX forms: listen for the `form:valid` event on the form and call
 * preventDefault() to take over submission. The event fires after all
 * client-side validation passes and before the form actually submits.
 *
 * Per-field opt-out: a field with `data-suppress-required-message` will not
 * show the "valueMissing" error on blur (the snippet's `suppress_required_message`
 * param emits this attribute). Submit still blocks normally — callers that want
 * empty submissions to be silently impossible should also disable their submit
 * button while invalid (see email-signup.js).
 *
 * Loaded as type="module" so it dedupes across multiple section renders.
 */

(function () {
  'use strict';

  const MESSAGES = {
    valueMissing: 'This field is required.',
    typeMismatch: {
      email: 'Please enter a valid email address.',
      url: 'Please enter a valid URL.',
      default: 'Please enter a valid value.',
    },
    patternMismatch: 'Please match the requested format.',
    tooShort: 'Please enter at least {minLength} characters.',
    tooLong: 'Please enter no more than {maxLength} characters.',
    rangeUnderflow: 'Please enter a value of at least {min}.',
    rangeOverflow: 'Please enter a value no greater than {max}.',
  };

  const messageFor = (field) => {
    const v = field.validity;
    if (v.customError) return field.validationMessage;
    if (v.valueMissing) return MESSAGES.valueMissing;
    if (v.typeMismatch) {
      return MESSAGES.typeMismatch[field.type] || MESSAGES.typeMismatch.default;
    }
    if (v.patternMismatch) return MESSAGES.patternMismatch;
    if (v.tooShort) return MESSAGES.tooShort.replace('{minLength}', field.minLength);
    if (v.tooLong) return MESSAGES.tooLong.replace('{maxLength}', field.maxLength);
    if (v.rangeUnderflow) return MESSAGES.rangeUnderflow.replace('{min}', field.min);
    if (v.rangeOverflow) return MESSAGES.rangeOverflow.replace('{max}', field.max);
    return field.validationMessage;
  };

  const errorElFor = (field) => {
    const id = field.getAttribute('aria-describedby');
    return id ? document.getElementById(id) : null;
  };

  const showError = (field) => {
    const el = errorElFor(field);
    if (el) el.textContent = messageFor(field);
    field.setAttribute('aria-invalid', 'true');
  };

  const isOnlyValueMissing = (field) =>
    field.validity.valueMissing &&
    !field.validity.patternMismatch &&
    !field.validity.typeMismatch &&
    !field.validity.customError;

  const clearError = (field) => {
    const el = errorElFor(field);
    if (el) el.textContent = '';
    field.removeAttribute('aria-invalid');
  };

  const checkConfirm = (field) => {
    const matchId = field.getAttribute('data-confirm');
    if (!matchId) return;
    const target = document.getElementById(matchId);
    if (!target) return;
    field.setCustomValidity(field.value !== target.value ? 'Passwords do not match.' : '');
  };

  const fieldsOf = (form) => form.querySelectorAll('input, textarea, select');

  const setupForm = (form) => {
    if (form.hasAttribute('data-validate-init')) return;
    form.setAttribute('data-validate-init', '');
    form.setAttribute('novalidate', '');

    fieldsOf(form).forEach((field) => {
      const suppressesRequired = field.hasAttribute('data-suppress-required-message');

      field.addEventListener('blur', () => {
        checkConfirm(field);
        if (field.checkValidity()) {
          clearError(field);
          return;
        }
        if (suppressesRequired && isOnlyValueMissing(field)) {
          clearError(field);
          return;
        }
        showError(field);
      });

      field.addEventListener('input', () => {
        if (field.getAttribute('aria-invalid') !== 'true') return;
        checkConfirm(field);
        if (field.checkValidity()) clearError(field);
        else showError(field);
      });
    });

    form.addEventListener('submit', (e) => {
      let firstInvalid = null;
      fieldsOf(form).forEach((field) => {
        checkConfirm(field);
        if (field.checkValidity()) {
          clearError(field);
        } else {
          showError(field);
          if (!firstInvalid) firstInvalid = field;
        }
      });

      if (firstInvalid) {
        e.preventDefault();
        firstInvalid.focus();
        return;
      }

      const validEvent = new CustomEvent('form:valid', { cancelable: true, bubbles: true });
      const proceed = form.dispatchEvent(validEvent);
      if (!proceed) e.preventDefault();
    });
  };

  const init = () => {
    document.querySelectorAll('form[data-validate]').forEach(setupForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
